import React from 'react'
import { Link } from '@tanstack/react-router'

interface MarkdownProps {
  content: string | null | undefined
  className?: string
}

function decodeHtmlEntities(str: string): string {
  return str
    .replace(/&amp;/g, '&')
    .replace(/&#8217;|&rsquo;/g, '’')
    .replace(/&#8216;|&lsquo;/g, '‘')
    .replace(/&#8220;|&ldquo;/g, '“')
    .replace(/&#8221;|&rdquo;/g, '”')
    .replace(/&quot;/g, '"')
    .replace(/&#039;|&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&ndash;/g, '–')
    .replace(/&mdash;/g, '—')
    .replace(/&hellip;/g, '…')
}

function normalizeMayparfumUrl(rawUrl: string): string {
  const url = rawUrl.trim()

  // Match MayParfum original site links and route internally in SPA
  const brandMatch = url.match(/^(?:https?:\/\/(?:www\.)?mayparfum\.com)?\/brand\/([a-zA-Z0-9_-]+)\/?$/i)
  if (brandMatch) {
    const slug = brandMatch[1].toLowerCase().replace(/_/g, '-')
    return `/brands/${slug}`
  }

  const perfumerMatch = url.match(/^(?:https?:\/\/(?:www\.)?mayparfum\.com)?\/perfumer\/([a-zA-Z0-9_-]+)\/?$/i)
  if (perfumerMatch) {
    const slug = perfumerMatch[1].toLowerCase().replace(/_/g, '-')
    return `/specs/perfumer/${slug}`
  }

  const productMatch = url.match(/^(?:https?:\/\/(?:www\.)?mayparfum\.com)?\/product\/([a-zA-Z0-9_-]+)\/?$/i)
  if (productMatch) {
    const slug = productMatch[1].toLowerCase()
    return `/catalog/${slug}`
  }

  return url
}

function htmlToMarkdown(input: string): string {
  let text = input

  // Strip dangerous tags completely
  text = text.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
  text = text.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
  text = text.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')

  // Convert <a href="...">label</a> to markdown link [label](url)
  text = text.replace(/<a\s+[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi, (_, href, label) => {
    const cleanHref = normalizeMayparfumUrl(decodeHtmlEntities(href))
    return `[${label.trim()}](${cleanHref})`
  })

  // Convert <strong> / <b> to **bold**
  text = text.replace(/<(?:strong|b)\b[^>]*>(.*?)<\/(?:strong|b)>/gi, '**$1**')

  // Convert <em> / <i> to *italic*
  text = text.replace(/<(?:em|i)\b[^>]*>(.*?)<\/(?:em|i)>/gi, '*$1*')

  // Convert <br> or <br/> to \n
  text = text.replace(/<br\s*\/?>/gi, '\n')

  // Convert <p>...</p> to paragraphs
  text = text.replace(/<p\b[^>]*>(.*?)<\/p>/gi, '$1\n\n')

  // Remove any remaining HTML tags safely
  text = text.replace(/<[^>]+>/g, '')

  return decodeHtmlEntities(text)
}

function parseInline(text: string): React.ReactNode[] {
  // Matches bold **text**, italic *text*, or links [label](url)
  const regex = /(\*\*.*?\*\*|\*[^*]+?\*|\[.*?\]\(.*?\))/g
  const parts = text.split(regex)

  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
      return (
        <strong key={i} className="font-semibold text-slate-100 dark:text-white">
          {parseInline(part.slice(2, -2))}
        </strong>
      )
    }

    if (part.startsWith('*') && part.endsWith('*') && part.length >= 2 && !part.startsWith('**')) {
      return (
        <em key={i} className="italic text-slate-200">
          {parseInline(part.slice(1, -1))}
        </em>
      )
    }

    if (part.startsWith('[') && part.includes('](') && part.endsWith(')')) {
      const closeBracket = part.indexOf('](')
      const label = part.slice(1, closeBracket)
      const rawUrl = part.slice(closeBracket + 2, -1)
      const url = normalizeMayparfumUrl(rawUrl)

      // Internal SPA link
      if (url.startsWith('/')) {
        return (
          <Link
            key={i}
            to={url}
            className="text-[#c9a24b] font-medium underline underline-offset-2 hover:text-[#d8b25c] hover:no-underline transition"
          >
            {label}
          </Link>
        )
      }

      // External safe link check
      let safe = false
      try {
        const parsed = new URL(url, window.location.href)
        safe = ['https:', 'http:', 'mailto:'].includes(parsed.protocol)
      } catch {
        // invalid URL
      }

      if (!safe) return <React.Fragment key={i}>{label}</React.Fragment>

      return (
        <a
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#c9a24b] font-medium underline underline-offset-2 hover:text-[#d8b25c] hover:no-underline transition"
        >
          {label}
        </a>
      )
    }

    return part
  })
}

export function Markdown({ content, className }: MarkdownProps) {
  if (!content) return null

  // Pre-process HTML tags into sanitized Markdown format
  const sanitizedMarkdown = htmlToMarkdown(content)

  // Normalize line endings
  const normalized = sanitizedMarkdown.replace(/\r\n/g, '\n')
  // Split into blocks by double newlines
  const blocks = normalized.split(/\n\n+/)

  return (
    <div className={className || 'space-y-3.5 leading-relaxed'}>
      {blocks.map((block, index) => {
        const trimmed = block.trim()
        if (!trimmed) return null

        // H1 Heading
        if (trimmed.startsWith('# ')) {
          return (
            <h1 key={index} className="text-xl font-bold mt-4 mb-2 text-slate-100 dark:text-white">
              {parseInline(trimmed.slice(2))}
            </h1>
          )
        }

        // H2 Heading
        if (trimmed.startsWith('## ')) {
          return (
            <h2 key={index} className="text-lg font-bold mt-3 mb-2 text-slate-100 dark:text-white">
              {parseInline(trimmed.slice(3))}
            </h2>
          )
        }

        // H3 Heading
        if (trimmed.startsWith('### ')) {
          return (
            <h3 key={index} className="text-base font-bold mt-3 mb-1 text-slate-200 dark:text-gray-100">
              {parseInline(trimmed.slice(4))}
            </h3>
          )
        }

        // List block (all lines in the block start with "- " or "* ")
        const lines = trimmed.split('\n')
        if (
          lines.length > 0 &&
          lines.every((line) => line.trim().startsWith('- ') || line.trim().startsWith('* '))
        ) {
          return (
            <ul key={index} className="list-disc pl-5 space-y-1 my-2 text-slate-300 dark:text-gray-300">
              {lines.map((line, lIdx) => (
                <li key={lIdx}>{parseInline(line.trim().slice(2))}</li>
              ))}
            </ul>
          )
        }

        // Default: Paragraph
        const linesOfPara = trimmed.split('\n')
        return (
          <p key={index} className="leading-relaxed">
            {linesOfPara.map((line, lIdx) => (
              <React.Fragment key={lIdx}>
                {lIdx > 0 && <br />}
                {parseInline(line)}
              </React.Fragment>
            ))}
          </p>
        )
      })}
    </div>
  )
}
